/**
 * @fileoverview Real backend calls for the student "My tasks" module.
 * Endpoints are the legacy student-safe routes. Axios humps
 * interceptor delivers camelCase. Raw shapes and pure adapters live in
 * ./adapters; the source/hooks own error handling.
 *
 * Single-deliverable contract (AR-741): the backend still creates a throwaway
 * OPEN delivery on every GET /api/student_tasks/:id, so we prefer the id of
 * `studentTaskDeliveryLast` (the student's real DONE delivery) — uploads,
 * removals and the submit PUT all target that same record.
 */

import { api } from 'helpers'
import type {
  DeliverableDetail,
  DeliveryLink,
  FinalAssessmentDetail,
  ScoreInput,
  SelfAssessmentDetail,
  StudentTask,
} from '../types'
import { adaptPendency, adaptScore, DONE_STATUSES, mapDriveFiles, mapFiles, mapLinks, str } from './adapters'
import type { RawPendency } from './adapters'

export { adaptPendency }

/**
 * Student pendencies index. NOTE: the Rails route is
 * `resources :pendencies, path: 'tasks'` → the URL is `/tasks.json`, NOT
 * `/pendencies.json` (the legacy front used the old path). Unmatched paths are
 * forwarded to the Rails backend by the dev proxy's `'/*'` catch-all.
 */
export async function fetchPendencies(): Promise<StudentTask[]> {
  const tasks: StudentTask[] = []
  const MAX_PAGES = 50
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const { data } = await api.get('/tasks.json', { params: { by_student: 1, page } })
    const list = (data?.pendencies ?? []) as RawPendency[]
    list.forEach(raw => {
      const task = adaptPendency(raw)
      if (task) tasks.push(task)
    })
    if (!data?.pagy?.next) break
  }
  return tasks
}

export async function fetchDeliverableDetail(id: string): Promise<DeliverableDetail> {
  const { data } = await api.get(`/api/student_tasks/${id}`)
  const activity = data?.sessionMeetingActivity ?? {}
  const last = data?.studentTaskDeliveryLast
  return {
    identifierId: id,
    title: str(data?.title),
    description: str(data?.description),
    deadline: data?.deadline || null,
    overdue: Boolean(data?.overdue),
    done: DONE_STATUSES.has(str(data?.status)),
    projectId: str(data?.projectId),
    // Reuse the existing done delivery when there is one (AR-741); the raw
    // studentTaskDeliveryId is a fresh open record created by the GET itself.
    studentTaskDeliveryId: str(last?.id || data?.studentTaskDeliveryId),
    projectName: str(activity.projectName),
    lessonTitle: str(activity.sessionMeetingTitle || activity.title),
    lessonDate: str(activity.sessionMeetingDate),
    tutor: str(activity.tutor),
    taskFiles: mapFiles(data?.planningFiles),
    // Links of the EFFECTIVE delivery — the done one when it exists (AR-741),
    // else the current open one. Top-level key is `student_task_delivery_links`
    // (camelized by humps; plain `links` was already taken by HATEOAS links).
    links: mapLinks(last ? last.links : data?.studentTaskDeliveryLinks),
    // Same effective-delivery rule for the Classroom-mirrored Drive
    // attachments (AR-805); key `student_task_delivery_google_drive_files`.
    googleDriveFiles: mapDriveFiles(last ? last.googleDriveFiles : data?.studentTaskDeliveryGoogleDriveFiles),
    lastDelivery: last
      ? {
        id: str(last.id),
        description: str(last.description),
        createdAt: str(last.createdAt),
        files: mapFiles(last.planningFiles),
      }
      : null,
  }
}

export async function fetchSelfAssessmentDetail(id: string): Promise<SelfAssessmentDetail> {
  const { data } = await api.get(`/api/students/my_assessments/${id}`)
  return {
    identifierId: id,
    assessmentId: str(data?.assessmentId),
    title: str(data?.title),
    date: str(data?.date),
    projectId: str(data?.projectId),
    projectName: str(data?.projectName),
    journalStudentId: str(data?.journalStudentId),
    journal: str(data?.journalStudent),
    skills: (data?.skillsAssessmentScores ?? []).map(adaptScore),
    contents: (data?.contentsAssessmentScores ?? []).map(adaptScore),
  }
}

export async function fetchFinalAssessmentDetail(id: string): Promise<FinalAssessmentDetail> {
  const { data } = await api.get(`/projects/${id}/final_assessments.json`)
  return {
    identifierId: id,
    finalAssessmentId: str(data?.finalAssessmentId),
    projectId: id,
    projectName: str(data?.name),
    journalStudentId: str(data?.journalStudentId),
    journal: str(data?.journalStudent),
    skills: (data?.specificSkillFinalAssessmentScores ?? []).map(adaptScore),
    contents: (data?.contentFinalAssessmentScores ?? []).map(adaptScore),
  }
}

export async function submitDeliverable(detail: DeliverableDetail, description: string): Promise<void> {
  await api.put(`/api/student_task_deliveries/${detail.studentTaskDeliveryId}`, { description })
}

/**
 * Attaches a link to the effective delivery (AR-677):
 * POST /api/student_task_deliveries/:id/links {link: {url}} → 201 {id, url,
 * createdAt}. 422 (invalid/broken URL) propagates for inline handling. The
 * `helpers/api` interceptor RESOLVES 401 responses, so anything that is not a
 * real 201 with the created link in the body throws instead of "succeeding".
 */
export async function addDeliverableLink(detail: DeliverableDetail, url: string): Promise<DeliveryLink> {
  const response = await api.post(
    `/api/student_task_deliveries/${detail.studentTaskDeliveryId}/links`,
    { link: { url } },
  )
  const link = {
    id: str(response?.data?.id),
    url: str(response?.data?.url),
    createdAt: str(response?.data?.createdAt),
  }
  if (response?.status !== 201 || !link.id) {
    throw new Error(`Unexpected response adding deliverable link (status ${response?.status})`)
  }
  return link
}

/**
 * Removes a link from the effective delivery (AR-677): DELETE → 204. A
 * resolved non-204 (e.g. the interceptor-resolved 401) throws.
 */
export async function removeDeliverableLink(detail: DeliverableDetail, linkId: string): Promise<void> {
  const response = await api.delete(
    `/api/student_task_deliveries/${detail.studentTaskDeliveryId}/links/${linkId}`,
  )
  if (response?.status !== 204) {
    throw new Error(`Unexpected response removing deliverable link (status ${response?.status})`)
  }
}

export async function submitSelfAssessment(
  detail: SelfAssessmentDetail,
  scores: readonly ScoreInput[],
  journal: string,
): Promise<void> {
  await api.patch('/api/students/update_report_student', {
    assessment_id: detail.assessmentId,
    assessment: {
      assessment_id: detail.assessmentId,
      journal_description: journal,
      assessment_scores: scores.map(s => ({ assessment_score_id: s.id, student_score: s.score })),
    },
  })
}

export async function submitFinalAssessment(
  detail: FinalAssessmentDetail,
  scores: readonly ScoreInput[],
  journal: string,
): Promise<void> {
  // Students submit one score at a time via the student-permitted endpoint
  // (final_assessment_scores#update → update_student). The bulk
  // /update_final_assessment_scores is educator-only (returns 403 for students).
  // Score 0 IS sent: it is how the picker clears a score, and skipping it would
  // leave the drawer showing an empty hand while the old value survives on the
  // backend (the model only rejects a nil student_score, not a zero one).
  await Promise.all(
    scores.map(s =>
      api.patch(`/final_assessment_scores/${s.id}`, { final_assessment_score: { score: s.score } }),
    ),
  )
  if (detail.journalStudentId && journal.trim().length > 0) {
    await api.patch(`/journals/${detail.journalStudentId}`, { journal: { description: journal } })
  }
}
